import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { GitMerge, ArrowRight, Warning } from "@phosphor-icons/react";
import api from "../lib/api";
import { brl } from "../lib/format";

function HandlePicker({ label, value, onChange, customers, exclude, testid }) {
  const [q, setQ] = useState("");
  const opts = useMemo(() => {
    const f = q.trim().toLowerCase();
    return customers
      .filter(c => c.handle !== exclude)
      .filter(c => !f || c.handle.toLowerCase().includes(f) || (c.full_name || "").toLowerCase().includes(f))
      .slice(0, 200);
  }, [customers, q, exclude]);
  return (
    <div>
      <label className="label-brutal">{label}</label>
      <input data-testid={`${testid}-search`} className="input-brutal mb-2" placeholder="Buscar @ ou nome…" value={q} onChange={(e) => setQ(e.target.value)} />
      <select data-testid={`${testid}-select`} className="input-brutal" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Selecione…</option>
        {opts.map(c => <option key={c.id} value={c.handle}>{c.handle} — {c.full_name}</option>)}
      </select>
    </div>
  );
}

function StatsCard({ title, s, tone, testid }) {
  if (!s) return null;
  return (
    <div data-testid={testid} className={`card-brutal p-4 ${tone}`}>
      <p className="label-brutal !mb-1">{title}</p>
      <p className="font-heading text-2xl font-black break-all">{s.handle}</p>
      <p className="text-sm text-brand-ink2">{s.customer?.full_name || "Sem cadastro"}</p>
      <dl className="grid grid-cols-2 gap-2 mt-3 text-sm">
        <dt>Pedidos</dt><dd className="font-bold text-right">{s.orders_count}</dd>
        <dt>Lives</dt><dd className="font-bold text-right">{s.lives_count}</dd>
        <dt>Total</dt><dd className="font-bold text-right">{brl(s.total)}</dd>
        <dt>Em aberto</dt><dd className="font-bold text-right">{brl(s.unpaid)}</dd>
        <dt>Caixinhas</dt><dd className="font-bold text-right">{s.shipments_count}</dd>
      </dl>
    </div>
  );
}

export default function MergeHandles() {
  const [customers, setCustomers] = useState([]);
  const [source, setSource] = useState("");
  const [target, setTarget] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadCustomers = () => api.get("/customers").then(r => setCustomers(r.data)).catch(() => toast.error("Erro ao carregar clientes"));
  useEffect(() => { loadCustomers(); }, []);

  useEffect(() => {
    setPreview(null);
    setConfirming(false);
    if (!source || !target) return;
    api.get("/customers/merge/preview", { params: { source, target } })
      .then(r => setPreview(r.data))
      .catch(err => toast.error(err?.response?.data?.detail || "Erro na prévia"));
  }, [source, target]);

  const merge = async () => {
    setSaving(true);
    try {
      await api.post("/customers/merge", { source_handle: source, target_handle: target });
      toast.success(`${source} unificada em ${target}`);
      setSource("");
      setTarget("");
      loadCustomers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao unificar");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="merge-handles-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-4xl font-black flex items-center gap-2"><GitMerge weight="bold" /> Unificação</h1>
        <p className="text-sm text-brand-ink2 mt-1">Junte duas @arrobas da mesma cliente. Pedidos, pagamentos, desistências, caixinhas e acessos da @ de origem passam para a @ de destino, e o cadastro de origem é excluído.</p>
      </div>
      <div className="card-brutal p-4 bg-white grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-end">
        <HandlePicker label="@ de origem (será removida)" value={source} onChange={setSource} customers={customers} exclude={target} testid="merge-source" />
        <ArrowRight weight="bold" size={28} className="hidden md:block mb-3" />
        <HandlePicker label="@ de destino (será mantida)" value={target} onChange={setTarget} customers={customers} exclude={source} testid="merge-target" />
      </div>
      {preview && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StatsCard title="Origem" s={preview.source} tone="bg-brand-peach" testid="merge-preview-source" />
            <StatsCard title="Destino" s={preview.target} tone="bg-brand-yellow" testid="merge-preview-target" />
          </div>
          {!confirming ? (
            <button data-testid="merge-start-btn" onClick={() => setConfirming(true)} className="btn-pink h-[52px]">
              <GitMerge weight="bold" size={16} /> Unificar
            </button>
          ) : (
            <div data-testid="merge-confirm-box" className="card-brutal p-4 bg-white flex flex-wrap items-center gap-3">
              <Warning weight="bold" size={20} className="text-red-600" />
              <p className="text-sm font-bold flex-1">Confirma mover tudo de {source} para {target} e excluir {source}? Esta ação não pode ser desfeita.</p>
              <button data-testid="merge-cancel-btn" onClick={() => setConfirming(false)} className="btn-white h-[44px]">Cancelar</button>
              <button data-testid="merge-confirm-btn" onClick={merge} disabled={saving} className="btn-pink h-[44px] disabled:opacity-60">
                {saving ? "Unificando…" : "Confirmar unificação"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
